import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Table, Button, Alert } from 'reactstrap';
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import {
  getVersionsByModel,
  createVersion,
  updateVersion,
  deleteVersionById,
  setCreateVersion,
  setUpdateVersion,
  setDeleteVersionById,
} from '../../redux/actions/version';

import Loading from '../../components/Loading/Loading';
import ConfirmationModal from '../../components/Modal/ConfirmationModal';
import CustomBreadcrumb from '../../components/Breadcrumb/Breadcrumb';
import VersionModal from './subcomponents/VersionModal';

function VersionList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { brandId, modelId } = useParams();
  const location = useLocation();
  const brandName = location.state?.brandName || 'Marca';
  const modelName = location.state?.modelName || 'Modelo';

  const listState = useSelector((state) => state.version.data);
  const createState = useSelector((state) => state.version.create);
  const updateState = useSelector((state) => state.version.update);
  const deleteState = useSelector((state) => state.version.deleteById);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('');
  const [showAlert, setShowAlert] = useState(false);

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingVersion, setEditingVersion] = useState(null);

  const refresh = useCallback(() => {
    setLoading(true);
    dispatch(getVersionsByModel(modelId));
  }, [dispatch, modelId]);

  const handleCreate = useCallback(
    (name) => {
      setLoading(true);
      dispatch(createVersion(modelId, name));
    },
    [dispatch, modelId],
  );

  const handleEdit = useCallback(
    (id, name) => {
      setLoading(true);
      dispatch(updateVersion(id, name));
    },
    [dispatch],
  );

  const handleDelete = useCallback(
    (id) => {
      setLoading(true);
      dispatch(deleteVersionById(id));
    },
    [dispatch],
  );

  // Modal control methods
  const openCreateModal = () => {
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
  };

  const openEditModal = (version) => {
    setEditingVersion(version);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditingVersion(null);
    setIsEditModalOpen(false);
  };

  const onVersionCreate = (name) => {
    handleCreate(name);
    closeCreateModal();
  };

  const onVersionEdit = (name) => {
    if (editingVersion) {
      handleEdit(editingVersion.id, name);
      closeEditModal();
    }
  };

  const navigateBack = () => {
    navigate(`/brands/${brandId}/models`, { state: brandName });
  };

  const handleAlert = (type, message) => {
    setAlertType(type);
    setAlertMessage(message);
    setShowAlert(true);
    const timer = setTimeout(() => setShowAlert(false), 3000);
    return () => clearTimeout(timer);
  };

  const clearReduxState = useCallback(
    (actionCreator, delay = 3500) => {
      setTimeout(() => {
        dispatch(actionCreator(null));
      }, delay);
    },
    [dispatch],
  );

  useEffect(() => {
    document.title = `BGL - Versiones de ${modelName}`;
    refresh();
  }, [refresh, modelName]);

  // Efecto separado para manejar respuestas de acciones
  useEffect(() => {
    if (listState) {
      setData(listState.data || []);
      setLoading(false);
    }
  }, [listState]);

  useEffect(() => {
    if (createState) {
      handleAlert(createState.status, createState.message);
      setLoading(false);
      if (createState.status === 'success') {
        dispatch(getVersionsByModel(modelId));
      }
      clearReduxState(setCreateVersion);
    }
  }, [createState, clearReduxState, dispatch, modelId]);

  useEffect(() => {
    if (updateState) {
      handleAlert(updateState.status, updateState.message);
      setLoading(false);
      if (updateState.status === 'success') {
        dispatch(getVersionsByModel(modelId));
      }
      clearReduxState(setUpdateVersion);
    }
  }, [updateState, clearReduxState, dispatch, modelId]);

  useEffect(() => {
    if (deleteState) {
      handleAlert(deleteState.status, deleteState.message);
      setLoading(false);
      if (deleteState.status === 'success') {
        dispatch(getVersionsByModel(modelId));
      }
      clearReduxState(setDeleteVersionById);
    }
  }, [deleteState, clearReduxState, dispatch, modelId]);

  const breadcrumbItems = [
    { label: 'Marcas', path: '/brands' },
    { label: brandName, path: `/brands/${brandId}/models` },
    { label: modelName, path: `/brands/${brandId}/models/${modelId}/versions` },
    { label: 'Versiones', path: null },
  ];

  return loading ? (
    <Loading />
  ) : (
    <>
      <Container>
        <Alert
          color={alertType}
          isOpen={showAlert}
          toggle={() => setShowAlert(false)}
        >
          {alertMessage}
        </Alert>
      </Container>

      <CustomBreadcrumb items={breadcrumbItems} onNavigate={navigateBack} />

      <Container className="container-border p-3">
        <article>
          <Button
            className="float-right mb-2"
            size="sm"
            onClick={openCreateModal}
          >
            <i className="fa fa-plus mr-3"></i>
            <span>Crear versión</span>
          </Button>
          <Button
            className="float-right mb-2 mr-2"
            size="sm"
            onClick={() => refresh()}
          >
            <i className="fa fa-sync mr-3"></i>
            <span>Refrescar</span>
          </Button>
          <h2>Versiones de {modelName}</h2>
        </article>

        <Table hover>
          <thead>
            <tr>
              <th>ID</th>
              <th>Nombre</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {data.length === 0 && (
              <tr>
                <td colSpan="3" className="text-center">
                  No hay versiones disponibles
                </td>
              </tr>
            )}

            {data.map((row) => {
              return (
                <tr key={row.id}>
                  <td>{row.id}</td>
                  <td>{row.name}</td>
                  <td>
                    <>
                      <Button
                        className="mr-2"
                        size="sm"
                        onClick={() => openEditModal(row)}
                      >
                        <i className="fa fa-edit"></i>
                      </Button>
                      <ConfirmationModal
                        btnClass="btn btn-danger btn-sm"
                        iconClass="fa fa-trash"
                        title="Eliminar versión"
                        message="¿Estás seguro que quieres eliminar esta versión? Esta operación no se podrá deshacer."
                        action={() => handleDelete(row.id)}
                        argument={row.id}
                        successMessage="Eliminar"
                        cancelMessage="Cancelar"
                        btnSuccessState="danger"
                        btnCancelState="secondary"
                      />
                    </>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Container>

      {/* Create Version Modal */}
      <VersionModal
        isOpen={isCreateModalOpen}
        onToggle={closeCreateModal}
        title="Crear versión"
        onSubmit={onVersionCreate}
      />

      {/* Edit Version Modal */}
      <VersionModal
        isOpen={isEditModalOpen}
        onToggle={closeEditModal}
        title="Editar versión"
        defaultVersion={editingVersion}
        onSubmit={onVersionEdit}
      />
    </>
  );
}

export default VersionList;
